import _ from 'lodash';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';
import { Survey, SurveyPayload, SurveyTag } from '../features/motive/survey.types';

export enum PdfGenerateType {
  EXPERT = 'expert',
  LEADERSHIP = 'leader',
}

export interface MotiveWebReportData {
  participantName: string;
  reportType: PdfGenerateType;
  language: 'en' | 'de';
  createdAt: string;
  takeaways: Array<{
    title: string;
    score: number;
    description: string;
    action: string;
  }>;
  stories: Array<{
    id: string;
    title: string;
    imageLabel: string;
    text: string;
    tags: string[];
    detections: Array<{
      motive: string;
      confidence: number;
    }>;
  }>;
  motiveScores: Array<{
    motive: string;
    score: number;
    color: string;
    description: string;
  }>;
  nextSteps: string[];
}

export const sendOpenSurvey = async (surveys: SurveyPayload[], notSendEmail: boolean) => {
  try {
    const results = await API_V1.post(
      '/surveys',
      { newSurveys: surveys },
      {
        params: {
          sendEmail: !notSendEmail ? undefined : false,
        },
      }
    );
    return results.data;
  } catch (e) {
    Logger.trace('Error while sending survey invitations');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getAllSurveys = async () => {
  try {
    const results = await API_V1.get('/surveys');
    const surveys: Survey[] = [];
    for (const survey of results.data) {
      surveys.push(new Survey(survey));
    }
    return { data: surveys };
  } catch (e) {
    Logger.trace('Error while fetching surveys');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getSurveyTags = async () => {
  try {
    const response = await API_V1.get('/surveys/tags/with-surveys');
    const tags: SurveyTag[] = [];
    for (const tag of response.data.tags) {
      tags.push(new SurveyTag(tag));
    }
    return { data: tags };
  } catch (e) {
    Logger.trace('Error while getting survey tags');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addSurveyTag = async (name: string) => {
  try {
    const results = await API_V1.post('/surveys/tags', {
      name,
      groupType: 'classic',
    });
    return results.data._id ?? results.data.id;
  } catch (e) {
    Logger.trace('Error while adding new competence monitor tag');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const deleteSurveyTag = async (tagId: string) => {
  try {
    await API_V1.delete('/surveys/tags/' + tagId);
    return true;
  } catch (e) {
    Logger.trace('Error while deleting competence monitor tag %s', tagId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const editSurveyTag = async (tagId: string, name?: string, visible?: boolean) => {
  try {
    const res = await API_V1.patch('/surveys/tags/' + tagId, {
      name,
      visible,
    });
    return res.data;
  } catch (e) {
    Logger.trace('Error while editing competence monitor tag %s', tagId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const deleteSurvey = async (surveyId: string) => {
  try {
    await API_V1.delete('/surveys/' + surveyId);
    return true;
  } catch (e) {
    Logger.trace('Error while deleting survey %s', surveyId);
    Logger.error(e);
    return false;
  }
};

export const getSurveyReport = async (
  surveyId: string,
  pdfGenerateType: PdfGenerateType,
  compareProfileId?: string,
  language?: string
) => {
  try {
    const params: any = {
      type: pdfGenerateType,
    };
    if (compareProfileId) {
      params.profile = compareProfileId;
    }
    if (language) {
      params.language = language;
    }
    const response = await API_V1.get('/surveys/' + surveyId + '/report', {
      headers: {
        Accept: 'application/pdf',
      },
      responseType: 'blob',
      params,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting survey report for %s', surveyId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getSurveyWebReportLink = async (
  surveyId: string,
  reportType: PdfGenerateType = PdfGenerateType.EXPERT,
  language: 'de' | 'en' = 'de'
) => {
  try {
    const response = await API_V1.get(`/surveys/${surveyId}/report-link`, {
      params: {
        type: reportType,
        language,
      },
    });
    return response.data as { token: string; url: string; expiresIn: string };
  } catch (e) {
    Logger.trace('Error while creating survey web report link for %s', surveyId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getSurveyInviteLink = async (surveyId: string) => {
  try {
    const response = await API_V1.get(`/surveys/${surveyId}/invite-link`);
    return response.data as { token: string; url: string; expiresIn: number };
  } catch (e) {
    Logger.trace('Error while creating survey invite link for %s', surveyId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getPublicMotiveWebReport = async (token: string) => {
  try {
    const response = await API_V1.get(`/motive-report/${token}`);
    return { data: response.data as MotiveWebReportData };
  } catch (e) {
    Logger.trace('Error while fetching motive web report for token');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const askPublicMotiveWebReportChat = async (token: string, message: string) => {
  try {
    const response = await API_V1.post(`/motive-report/${token}/chat`, { message });
    return {
      data: response.data as {
        answer: string;
        reportType: PdfGenerateType;
        language: 'en' | 'de';
        userMessage?: { role: 'user'; content: string; createdAt?: string };
        assistantMessage?: { role: 'assistant'; content: string; createdAt?: string };
      },
    };
  } catch (e) {
    Logger.trace('Error while asking motive web report chat');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getPublicMotiveWebReportChatHistory = async (token: string) => {
  try {
    const response = await API_V1.get(`/motive-report/${token}/chat-history`);
    return {
      data: response.data as {
        messages: Array<{ role: 'user' | 'assistant'; content: string; createdAt?: string }>;
      },
    };
  } catch (e) {
    Logger.trace('Error while fetching motive web report chat history');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const resetPublicMotiveWebReportChatHistory = async (token: string) => {
  try {
    const response = await API_V1.delete(`/motive-report/${token}/chat-history`);
    return { data: (response.data ?? { success: true }) as { success: boolean } };
  } catch (e) {
    Logger.trace('Error while resetting motive web report chat history');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const moveSurveysToTag = async (sourceTagId: string, targetTagId: string, surveysIds: { id: string }[]) => {
  try {
    for (const surveyID of surveysIds) {
      await API_V1.post('/surveys/' + surveyID.id + '/tags', {
        tags: [targetTagId],
      });
      await API_V1.delete('/surveys/' + surveyID.id + '/tags', {
        data: { tags: [sourceTagId] },
      });
    }
    return true;
  } catch (e) {
    Logger.trace('Error while moving survey(s) to tag %s', targetTagId);
    Logger.error(e);
    return false;
  }
};

export const getSurveyById = async (key: string, tenantId: string) => {
  try {
    const response = await API_V1.get(`/public/surveys/${key}?tenantId=${tenantId}`);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while fetching survey %s', key);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getSurveyByInviteToken = async (token: string) => {
  try {
    const response = await API_V1.get(`/public/surveys/invite?token=${encodeURIComponent(token)}`);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while fetching survey by invite token');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const sendCompletedSurvey = async (
  key: string,
  tenantId: string,
  texts: string[],
  surveyAffil: number[],
  surveyAchieve: number[],
  surveyPower: number[]
) => {
  try {
    const response = await API_V1.post(`/public/surveys/${key}/submit?tenantId=${tenantId}`, {
      textResponses: texts,
      affiliation: surveyAffil,
      achieve: surveyAchieve,
      power: surveyPower,
    });
    return { data: !!response.data?.success };
  } catch (e) {
    Logger.trace('Error while sending survey %s', key);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const sendCompletedSurveyByInviteToken = async (
  token: string,
  texts: string[],
  surveyAffil: number[],
  surveyAchieve: number[],
  surveyPower: number[]
) => {
  try {
    const response = await API_V1.post(`/public/surveys/invite/submit?token=${encodeURIComponent(token)}`, {
      textResponses: texts,
      affiliation: surveyAffil,
      achieve: surveyAchieve,
      power: surveyPower,
    });
    return { data: !!response.data?.success };
  } catch (e) {
    Logger.trace('Error while sending survey via invite token');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
